import { AppLanguage, getPreferredLanguage } from '../language';

export interface LabelingOrderFlowI18nTemplate {
  shared: {
    back: string;
    endOrder: string;
    next: string;
    duplicateSuffix: string;
    requiredPrefix: string;
    optionalPrefix: string;
    requiredTag: string;
    optionalTag: string;
    searchPlaceholder: string;
    noMatchedOption: string;
    localTime: string;
  };
  labelManagement: {
    authWelcome: {
      title: string;
      description: string;
      loginAction: string;
    };
    hero: {
      badge: string;
      title: string;
      description: string;
      primaryAction: string;
      iconAlt: string;
    };
    commonFunctionsTitle: string;
    fullQuoteAction: string;
    erpWmsAction: string;
    devDialog: {
      title: string;
      message: string;
      confirmText: string;
    };
  };
  selectAddress: {
    title: string;
    newSenderAddress: string;
    defaultBadge: string;
    loading: string;
    deleteFailed: string;
    loadFailed: string;
  };
  orderMethod: {
    title: string;
    single: {
      title: string;
      details: string;
    };
    batch: {
      title: string;
      details: string;
    };
  };
  addAddress: {
    titleCreate: string;
    titleEdit: string;
    description: string;
    senderInfoTitle: string;
    setDefault: string;
    senderNameLabel: string;
    senderNamePlaceholder: string;
    phoneLabel: string;
    phonePlaceholder: string;
    phonePopupPlaceholder: string;
    addressInfoTitle: string;
    streetLabel: string;
    streetPlaceholder: string;
    roomLabel: string;
    cityLabel: string;
    cityPlaceholder: string;
    stateLabel: string;
    statePlaceholder: string;
    statePopupPlaceholder: string;
    zipLabel: string;
    zipPlaceholder: string;
    save: string;
    saving: string;
    validation: {
      phoneMinDigits: string;
      phoneInvalidChars: string;
      cityLettersOnly: string;
      stateRequired: string;
      stateInvalid: string;
      zipInvalid: string;
      fixErrorsBeforeSave: string;
      selectValidState: string;
      duplicateAddress: string;
      saveFailed: string;
      saveError: string;
    };
  };
  selectCourier: {
    title: string;
    allOptionTitle: string;
    allOptionDetails: string;
    retry: string;
    next: string;
    processFailedPrefix: string;
  };
  selectDeliveryTime: {
    title: string;
    anyOption: string;
    loadOptionsFailed: string;
    backToCourier: string;
    processing: string;
    viewQuote: string;
    next: string;
    creatingOrder: string;
    processOrderFailedPrefix: string;
  };
  overview: {
    title: string;
    orderCount: string;
    senderAddress: string;
    package: string;
    packageInfo: string;
    packageInfoWithDelete: string;
    recipientAddress: string;
    noPackageInfo: string;
    productInfo: string;
    courier: string;
    deliveryTime: string;
    generatingQuote: string;
    processing: string;
    backToEdit: string;
    authRequired: string;
    weightLb: string;
    deletePackageTitle: string;
    deletePackageMessage: string;
    confirmDelete: string;
    packageDetails: string;
    recipient: string;
    weight: string;
    dimensions: string;
    inch: string;
  };
}

export const TEMPLATES: Record<AppLanguage, LabelingOrderFlowI18nTemplate> = {
  zh: {
    shared: {
      back: '返回',
      endOrder: '结束下单',
      next: '下一步',
      duplicateSuffix: '复制',
      requiredPrefix: '必填项',
      optionalPrefix: '选填项',
      requiredTag: '必填',
      optionalTag: '选填',
      searchPlaceholder: '搜索...',
      noMatchedOption: '未找到匹配的选项',
      localTime: '本地时间',
    },
    labelManagement: {
      authWelcome: {
        title: '购买海外面单',
        description:
          '比较快递运费并购买面单。',
        loginAction: '立即登录',
      },
      hero: {
        badge: '超低价格',
        title: '购买美国快递面单',
        description:
          '按发货地址查询美国快递报价。',
        primaryAction: '开始下单',
        iconAlt: 'Boxes',
      },
      commonFunctionsTitle: '常用功能',
      fullQuoteAction: '获得完整报价单',
      erpWmsAction: '在ERP或WMS中购买海海面单',
      devDialog: {
        title: '功能开发中',
        message: '此功能正在开发中，敬请期待！',
        confirmText: '知道了',
      },
    },
    selectAddress: {
      title: '选择发货地址',
      newSenderAddress: '+ 发货地址',
      defaultBadge: '默认',
      loading: 'Loading...',
      deleteFailed: 'Failed to delete address',
      loadFailed: 'Failed to load addresses',
    },
    orderMethod: {
      title: '选择下单方式',
      single: {
        title: '逐一下单',
        details: '手动填写收货地址和包裹信息',
      },
      batch: {
        title: '批量下单',
        details: '上传包含收货地址和包裹信息的表格',
      },
    },
    addAddress: {
      titleCreate: '新建发货地址',
      titleEdit: '编辑发货地址',
      description: '填写地址信息以便完成订单',
      senderInfoTitle: '发货人信息',
      setDefault: '设为默认地址',
      senderNameLabel: '发货人姓名',
      senderNamePlaceholder: '',
      phoneLabel: '手机号',
      phonePlaceholder: '',
      phonePopupPlaceholder: '请输入至少10位数字的手机号，可以包含()-和空格',
      addressInfoTitle: '地址信息',
      streetLabel: '街道和门牌号',
      streetPlaceholder: '街道与门牌号',
      roomLabel: '房间号 (选填)',
      cityLabel: '城市',
      cityPlaceholder: '',
      stateLabel: '州',
      statePlaceholder: '选择州',
      statePopupPlaceholder: '请从列表中选择州',
      zipLabel: '邮编',
      zipPlaceholder: '',
      save: '保存',
      saving: '保存中...',
      validation: {
        phoneMinDigits: '手机号必须至少包含10位数字',
        phoneInvalidChars: '手机号只能包含数字、空格和()-符号',
        cityLettersOnly: '城市名称只能包含英文字母',
        stateRequired: '州不能为空',
        stateInvalid: '请选择有效的州',
        zipInvalid: '邮编必须是5位或9位数字',
        fixErrorsBeforeSave: '请修正输入错误后再保存',
        selectValidState: '请从列表中选择有效的州',
        duplicateAddress: '已存在完全相同的发货地址，请修改后重试',
        saveFailed: '保存地址失败',
        saveError: '保存地址时出错',
      },
    },
    selectCourier: {
      title: '选择快递公司',
      allOptionTitle: '全部',
      allOptionDetails:
        '除UPS和USPS外，美国还有许多时效可以满足电商平台要求，运输轨迹可查，但价格相对低廉的中小快递公司，类似圆通，韵达。',
      retry: '重试',
      next: '下一步',
      processFailedPrefix: '处理选择失败: ',
    },
    selectDeliveryTime: {
      title: '指定快递时效',
      anyOption: '无所谓',
      loadOptionsFailed: 'Failed to load delivery time options',
      backToCourier: '返回选择快递公司',
      processing: '处理中...',
      viewQuote: '查看报价',
      next: '下一步',
      creatingOrder: '正在新建订单...',
      processOrderFailedPrefix: '处理订单失败: ',
    },
    overview: {
      title: '确认你的快递需求',
      orderCount: '共 {{count}} 个订单',
      senderAddress: '发件人地址',
      package: '包裹',
      packageInfo: '包裹信息',
      packageInfoWithDelete: '包裹信息 - 点击右侧菜单删除包裹',
      recipientAddress: '收件人地址',
      noPackageInfo: '没有包裹信息，请返回添加包裹',
      productInfo: '商品信息',
      courier: '快递公司',
      deliveryTime: '快递时效',
      generatingQuote: '生成报价',
      processing: '处理中...',
      backToEdit: '返回修改',
      authRequired: '请登录以访问此功能',
      weightLb: '磅',
      deletePackageTitle: '删除包裹',
      deletePackageMessage: '确定要删除这个包裹吗？',
      confirmDelete: '确认删除',
      packageDetails: '包裹信息：',
      recipient: '收件人',
      weight: '重量',
      dimensions: '尺寸',
      inch: '英寸',
    },
  },
  en: {
    shared: {
      back: 'Back',
      endOrder: 'End Order',
      next: 'Next',
      duplicateSuffix: 'Copy',
      requiredPrefix: 'Required',
      optionalPrefix: 'Optional',
      requiredTag: 'Required',
      optionalTag: 'Optional',
      searchPlaceholder: 'Search...',
      noMatchedOption: 'No matched options',
      localTime: 'Local Time',
    },
    labelManagement: {
      authWelcome: {
        title: 'Buy Shipping Labels',
        description:
          'Compare shipping rates and buy labels.',
        loginAction: 'Log In Now',
      },
      hero: {
        badge: 'Low Rates',
        title: 'Buy U.S. Shipping Labels',
        description:
          'Find U.S. shipping rates for your sender address.',
        primaryAction: 'Start Order',
        iconAlt: 'Boxes',
      },
      commonFunctionsTitle: 'Common Functions',
      fullQuoteAction: 'Get Full Price Quotes',
      erpWmsAction: 'Buy Labels in ERP or WMS',
      devDialog: {
        title: 'Feature In Development',
        message: 'This feature is under development. Please check back soon.',
        confirmText: 'OK',
      },
    },
    selectAddress: {
      title: 'Select Sender Address',
      newSenderAddress: '+ Sender address',
      defaultBadge: 'Default',
      loading: 'Loading...',
      deleteFailed: 'Failed to delete address',
      loadFailed: 'Failed to load addresses',
    },
    orderMethod: {
      title: 'Select Order Method',
      single: {
        title: 'Single Order',
        details: 'Manually fill recipient address and package details',
      },
      batch: {
        title: 'Batch Order',
        details: 'Upload a sheet with recipient address and package details',
      },
    },
    addAddress: {
      titleCreate: 'Create Sender Address',
      titleEdit: 'Edit Sender Address',
      description: 'Fill sender address details to continue order creation.',
      senderInfoTitle: 'Sender Info',
      setDefault: 'Set as default address',
      senderNameLabel: 'Sender Name',
      senderNamePlaceholder: '',
      phoneLabel: 'Phone Number',
      phonePlaceholder: '',
      phonePopupPlaceholder:
        'Enter at least 10 digits. You can include spaces, (), and -.',
      addressInfoTitle: 'Address Info',
      streetLabel: 'Street Address',
      streetPlaceholder: 'Street and building number',
      roomLabel: 'Apt / Unit (Optional)',
      cityLabel: 'City',
      cityPlaceholder: '',
      stateLabel: 'State',
      statePlaceholder: 'Select state',
      statePopupPlaceholder: 'Please select a state from the list',
      zipLabel: 'ZIP Code',
      zipPlaceholder: '',
      save: 'Save',
      saving: 'Saving...',
      validation: {
        phoneMinDigits: 'Phone number must contain at least 10 digits.',
        phoneInvalidChars: 'Phone number can only contain digits, spaces, (), and -.',
        cityLettersOnly: 'City can only contain letters.',
        stateRequired: 'State is required.',
        stateInvalid: 'Please select a valid state.',
        zipInvalid: 'ZIP code must be 5 or 9 digits.',
        fixErrorsBeforeSave: 'Please fix input errors before saving.',
        selectValidState: 'Please select a valid state from the list.',
        duplicateAddress: 'An identical sender address already exists. Please modify and retry.',
        saveFailed: 'Failed to save address.',
        saveError: 'An error occurred while saving the address.',
      },
    },
    selectCourier: {
      title: 'Select Courier',
      allOptionTitle: 'All',
      allOptionDetails:
        'Besides UPS and USPS, there are cost-effective regional carriers with trackable services that can meet e-commerce SLA requirements.',
      retry: 'Retry',
      next: 'Next',
      processFailedPrefix: 'Failed to process selection: ',
    },
    selectDeliveryTime: {
      title: 'Select Delivery Time',
      anyOption: 'Any',
      loadOptionsFailed: 'Failed to load delivery time options',
      backToCourier: 'Back to courier selection',
      processing: 'Processing...',
      viewQuote: 'View Quotes',
      next: 'Next',
      creatingOrder: 'Creating order...',
      processOrderFailedPrefix: 'Failed to process order: ',
    },
    overview: {
      title: 'Confirm Your Shipping Requirements',
      orderCount: '{{count}} orders total',
      senderAddress: 'Sender Address',
      package: 'Package',
      packageInfo: 'Package Info',
      packageInfoWithDelete: 'Package Info - Click menu on the right to delete package',
      recipientAddress: 'Recipient Address',
      noPackageInfo: 'No package info. Please go back and add packages.',
      productInfo: 'Product Info',
      courier: 'Courier',
      deliveryTime: 'Delivery Time',
      generatingQuote: 'Generate Quote',
      processing: 'Processing...',
      backToEdit: 'Back to Edit',
      authRequired: 'Please log in to access this feature',
      weightLb: 'lb',
      deletePackageTitle: 'Delete Package',
      deletePackageMessage: 'Are you sure you want to delete this package?',
      confirmDelete: 'Delete',
      packageDetails: 'Package details:',
      recipient: 'Recipient',
      weight: 'Weight',
      dimensions: 'Dimensions',
      inch: 'in',
    },
  },
};

export const LABELING_ORDER_FLOW_NAMESPACE = 'labelingOrderFlow';

export const LABELING_ORDER_FLOW_RESOURCE_KEY = 'template';

export const LABELING_ORDER_FLOW_I18N_RESOURCES: Record<
  AppLanguage,
  Record<typeof LABELING_ORDER_FLOW_RESOURCE_KEY, LabelingOrderFlowI18nTemplate>
> = {
  zh: {
    [LABELING_ORDER_FLOW_RESOURCE_KEY]: TEMPLATES.zh,
  },
  en: {
    [LABELING_ORDER_FLOW_RESOURCE_KEY]: TEMPLATES.en,
  },
};

export const getLabelingOrderFlowTemplate = (
  language: AppLanguage = getPreferredLanguage(),
): LabelingOrderFlowI18nTemplate => TEMPLATES[language] || TEMPLATES.zh;
